import Phaser from 'phaser';
import { PlayerPosition } from '@hokm/shared';
import { PlayerPositionConfig } from '../../../config/positions';

// Pre-defined control points for turn token bezier arcs
// Keys are "FROM_TO" position pairs, values are absolute control point coordinates
const TURN_TOKEN_ARCS_4P: Record<string, { x: number; y: number }> = {
  // Clockwise movement (outward arcs)
  SOUTH_EAST: { x: 580, y: 380 },
  EAST_NORTH: { x: 620, y: 60 },
  NORTH_WEST: { x: 180, y: 60 },
  WEST_SOUTH: { x: 20, y: 380 },
  // Counter-clockwise movement (same outward arcs)
  EAST_SOUTH: { x: 580, y: 380 },
  NORTH_EAST: { x: 620, y: 60 },
  WEST_NORTH: { x: 180, y: 60 },
  SOUTH_WEST: { x: 20, y: 380 },
};

const TURN_TOKEN_ARCS_3P: Record<string, { x: number; y: number }> = {
  // Triangle: SOUTH (bottom-center), WEST (top-left), NORTH (top-right)
  // SOUTH token ~(300, 290), WEST ~(105, 185), NORTH ~(695, 185)
  SOUTH_WEST: { x: -50, y: 350 }, // left side, moderate arc
  SOUTH_NORTH: { x: 850, y: 300 }, // far right, pulls arc away from table at start
  WEST_NORTH: { x: 400, y: -50 }, // top-center
  // Reverse directions
  WEST_SOUTH: { x: -50, y: 350 },
  NORTH_SOUTH: { x: 850, y: 300 },
  NORTH_WEST: { x: 400, y: -50 },
  // EAST fallbacks
  SOUTH_EAST: { x: 850, y: 300 },
  EAST_SOUTH: { x: 850, y: 300 },
  EAST_WEST: { x: 400, y: -50 },
  WEST_EAST: { x: 400, y: -50 },
  EAST_NORTH: { x: 850, y: -50 },
  NORTH_EAST: { x: 850, y: -50 },
};

const TURN_TOKEN_ARCS_2P: Record<string, { x: number; y: number }> = {
  // 2P: SOUTH (bottom-right) ↔ NORTH (top-left)
  // Arc around the table ellipse - push control points way out to clear the table
  // Player → Opponent: go around via TOP-RIGHT corner (far out)
  SOUTH_NORTH: { x: 850, y: -50 },
  SOUTH_WEST: { x: 850, y: -50 },
  SOUTH_EAST: { x: 850, y: -50 },
  // Opponent → Player: go around via BOTTOM-LEFT corner (far out)
  NORTH_SOUTH: { x: -50, y: 500 },
  WEST_SOUTH: { x: -50, y: 500 },
  EAST_SOUTH: { x: -50, y: 500 },
  // Opponent ↔ Opponent fallbacks
  NORTH_WEST: { x: 400, y: -50 },
  WEST_NORTH: { x: 400, y: -50 },
  NORTH_EAST: { x: 400, y: 500 },
  EAST_NORTH: { x: 400, y: 500 },
  WEST_EAST: { x: 400, y: 500 },
  EAST_WEST: { x: 400, y: -50 },
};

/**
 * 3D metallic Persian sun medallion that indicates whose turn it is.
 * Features elliptical shape, metallic highlights, bevel edge, and shadow.
 */
export class TurnToken {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | null = null;
  private glow: Phaser.GameObjects.Graphics | null = null;
  private pulseTween: Phaser.Tweens.Tween | null = null;
  private moveTween: Phaser.Tweens.Tween | null = null;
  private currentPosition: PlayerPosition | null = null;
  private currentNeedsAction: boolean = false;
  private getPositionConfig: () => Record<PlayerPosition, PlayerPositionConfig>;
  private getPlayerCount: () => number;

  constructor(
    scene: Phaser.Scene,
    getPositionConfig: () => Record<PlayerPosition, PlayerPositionConfig>,
    getPlayerCount: () => number
  ) {
    this.scene = scene;
    this.getPositionConfig = getPositionConfig;
    this.getPlayerCount = getPlayerCount;
  }

  private create(): Phaser.GameObjects.Container {
    const container = this.scene.add.container(0, 0);
    container.setDepth(15);

    const radius = 18;
    const perspectiveY = 0.85;

    // Shadow
    const shadow = this.scene.add.graphics();
    shadow.fillStyle(0x000000, 0.35);
    shadow.fillEllipse(3, 4, radius * 2.1, radius * 2.1 * perspectiveY);
    container.add(shadow);

    // Bevel edge
    const bevelEdge = this.scene.add.graphics();
    bevelEdge.fillStyle(0x3a2a1a, 1);
    bevelEdge.fillEllipse(0, 0, radius * 2.15, radius * 2.15 * perspectiveY);
    container.add(bevelEdge);

    // Body shadow
    const bodyBase = this.scene.add.graphics();
    bodyBase.fillStyle(0x8b6914, 1);
    bodyBase.fillEllipse(1, 1, radius * 2, radius * 2 * perspectiveY);
    container.add(bodyBase);

    // Main golden body
    const bodyMain = this.scene.add.graphics();
    bodyMain.fillStyle(0xd4af37, 1);
    bodyMain.fillEllipse(0, 0, radius * 1.95, radius * 1.95 * perspectiveY);
    container.add(bodyMain);

    // Highlight
    const highlight = this.scene.add.graphics();
    highlight.fillStyle(0xffd700, 0.6);
    highlight.fillEllipse(-3, -3, radius * 1.2, radius * 1.2 * perspectiveY);
    container.add(highlight);

    // Sun rays
    const rayLayer = this.scene.add.graphics();
    const rayCount = 8;
    const innerRadius = radius * 0.5;
    const outerRadius = radius * 0.9;

    for (let r = 0; r < rayCount; r++) {
      const angle = (r / rayCount) * Math.PI * 2 - Math.PI / 2;
      const nextAngle = ((r + 0.5) / rayCount) * Math.PI * 2 - Math.PI / 2;

      const innerX = Math.cos(angle) * innerRadius;
      const innerY = Math.sin(angle) * innerRadius * perspectiveY;
      const outerX = Math.cos(angle) * outerRadius;
      const outerY = Math.sin(angle) * outerRadius * perspectiveY;
      const tipX = Math.cos(nextAngle) * innerRadius;
      const tipY = Math.sin(nextAngle) * innerRadius * perspectiveY;

      rayLayer.fillStyle(0xffd700, 1);
      rayLayer.fillTriangle(innerX, innerY, outerX, outerY, tipX, tipY);
    }
    container.add(rayLayer);

    // Center circles
    const centerDark = this.scene.add.graphics();
    centerDark.fillStyle(0x8b6914, 1);
    centerDark.fillEllipse(0.5, 0.5, radius * 0.7, radius * 0.7 * perspectiveY);
    container.add(centerDark);

    const centerMain = this.scene.add.graphics();
    centerMain.fillStyle(0xb8942a, 1);
    centerMain.fillEllipse(0, 0, radius * 0.65, radius * 0.65 * perspectiveY);
    container.add(centerMain);

    const centerHighlight = this.scene.add.graphics();
    centerHighlight.fillStyle(0xd4af37, 0.7);
    centerHighlight.fillEllipse(-2, -2, radius * 0.35, radius * 0.35 * perspectiveY);
    container.add(centerHighlight);

    // Glow for pulse effect
    this.glow = this.scene.add.graphics();
    this.glow.fillStyle(0xffd700, 0);
    this.glow.fillEllipse(0, 0, radius * 2.4, radius * 2.4 * perspectiveY);
    container.add(this.glow);

    // Shimmer effect
    const shimmer = this.scene.add.graphics();
    shimmer.fillStyle(0xffffff, 0);
    shimmer.fillEllipse(-radius, 0, radius * 0.4, radius * 1.6 * perspectiveY);
    container.add(shimmer);

    this.scene.tweens.add({
      targets: shimmer,
      x: radius * 2,
      duration: 2500,
      ease: 'Sine.easeInOut',
      repeat: -1,
      repeatDelay: 1500,
      onUpdate: (tween) => {
        const progress = tween.progress;
        const alpha = Math.sin(progress * Math.PI) * 0.35;
        shimmer.clear();
        shimmer.fillStyle(0xffffff, alpha);
        shimmer.fillEllipse(0, 0, radius * 0.4, radius * 1.6 * perspectiveY);
      },
    });

    return container;
  }

  /**
   * Update turn token position and animation state.
   */
  update(targetPosition: PlayerPosition | null, needsAction: boolean): void {
    if (targetPosition === null) {
      if (this.container) {
        this.container.setVisible(false);
      }
      return;
    }

    if (!this.container) {
      this.container = this.create();
    }
    this.container.setVisible(true);

    // Get target coordinates from config
    const posConfig = this.getPositionConfig()[targetPosition];
    const targetX = posConfig.label.x + posConfig.turnToken.x;
    const targetY = posConfig.label.y + posConfig.turnToken.y;

    // Animate to new position
    if (this.currentPosition !== targetPosition) {
      if (this.moveTween) {
        this.moveTween.stop();
      }

      if (this.currentPosition === null) {
        this.container.setPosition(targetX, targetY);
      } else {
        const startX = this.container.x;
        const startY = this.container.y;

        // Look up pre-defined control point for this position transition
        const fromName = PlayerPosition[this.currentPosition];
        const toName = PlayerPosition[targetPosition];
        const key = `${fromName}_${toName}`;

        const playerCount = this.getPlayerCount();
        const arcs =
          playerCount === 2 ? TURN_TOKEN_ARCS_2P : playerCount === 3 ? TURN_TOKEN_ARCS_3P : TURN_TOKEN_ARCS_4P;

        const arc = arcs[key];
        const controlX = arc?.x ?? (startX + targetX) / 2;
        const controlY = arc?.y ?? (startY + targetY) / 2;

        const progress = { t: 0 };
        this.moveTween = this.scene.tweens.add({
          targets: progress,
          t: 1,
          duration: 450,
          ease: 'Sine.easeInOut',
          onUpdate: () => {
            const t = progress.t;
            const oneMinusT = 1 - t;
            const x = oneMinusT * oneMinusT * startX + 2 * oneMinusT * t * controlX + t * t * targetX;
            const y = oneMinusT * oneMinusT * startY + 2 * oneMinusT * t * controlY + t * t * targetY;
            if (this.container) {
              this.container.setPosition(x, y);
            }
          },
        });
      }
      this.currentPosition = targetPosition;
    }

    // Handle pulse animation
    if (needsAction !== this.currentNeedsAction) {
      this.currentNeedsAction = needsAction;

      if (this.pulseTween) {
        this.pulseTween.stop();
        this.pulseTween = null;
      }

      if (needsAction && this.glow) {
        this.glow.clear();
        this.glow.fillStyle(0xffd700, 0.4);
        this.glow.fillEllipse(0, 0, 52, 52 * 0.85);

        this.pulseTween = this.scene.tweens.add({
          targets: this.glow,
          scale: { from: 1, to: 1.4 },
          alpha: { from: 0.6, to: 0.2 },
          duration: 600,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      } else if (this.glow) {
        this.glow.clear();
        this.glow.setScale(1);
        this.glow.setAlpha(1);
      }
    }
  }

  reset(): void {
    this.currentPosition = null;
    this.currentNeedsAction = false;
    if (this.pulseTween) {
      this.pulseTween.stop();
      this.pulseTween = null;
    }
    if (this.moveTween) {
      this.moveTween.stop();
      this.moveTween = null;
    }
  }

  destroy(): void {
    this.reset();
    this.container?.destroy();
    this.container = null;
  }
}
